import React from "react";

export type BorderAnimationDirection = "fill" | "empty";

export type BorderAnimationColor =
    | "accent"
    | "line-base"
    | "foreground-base"
    | "ember"
    | (string & {});

const DEFAULT_TOP_SPEED = 200;
const DEFAULT_SIDE_SPEED = 200;
const DEFAULT_BOTTOM_SPEED = 200;

export interface BorderAnimationProps {
    /**
     * `"fill"` traces a border into existence (a line drawn around
     * the parent's padding box). `"empty"` retracts an existing
     * border in the reverse order.
     *
     * Sequence and easing per direction:
     * - `"fill"`:  top center→out (ease-in)
     *            → sides top→bottom (linear)
     *            → bottom corners→center (ease-out)
     * - `"empty"`: bottom center→corners split (ease-in)
     *            → sides bottom→top retract (linear)
     *            → top corners→center close (ease-out)
     *
     * Easing intentionally asymmetric: each step's ease "hands off"
     * to the next — the first edge eases-in (slow start), the middle
     * is linear (steady bridge), the last eases-out (settles). The
     * sequence reads as one continuous motion, not three separate
     * animations.
     */
    direction: BorderAnimationDirection;
    /**
     * Edge color. Maps to a Solomon color token (accent / line-base /
     * foreground-base / ember). Pass any other CSS color value
     * (`#fd4017`, `rgb(var(--color-...))`, `transparent`) and it's
     * used verbatim. Applied to all edges except top when `topColor`
     * is set.
     */
    color?: BorderAnimationColor;
    /**
     * Optional override for the top edge color. Falls through to
     * `color` when undefined. Use when a card has an accent-tinted
     * top + a different sides/bottom color — matches Solomon's
     * `border-t-accent-base border-line-base` resting card pattern.
     * Pass `"transparent"` to suppress the top edge visually while
     * keeping its keyframe timing intact (useful when a sibling
     * element draws the top edge separately).
     */
    topColor?: BorderAnimationColor;
    /**
     * Border thickness in pixels. Default `1`. With `borderWidth=2`,
     * the overlay sits `-inset-[2px]` outside the parent (instead of
     * the default `-inset-px`) so the 2px line aligns with a parent's
     * 2px border. Other widths aren't supported — the inset offset
     * is hardcoded to two cases by intent.
     */
    borderWidth?: 1 | 2;
    /**
     * Bumping this remounts each edge so the CSS keyframes replay
     * from frame 0. Use a stable, monotonically-increasing value
     * (e.g. a counter you bump on each animation request) — passing
     * `Math.random()` works but reframes every render and stops the
     * animation mid-flight.
     */
    triggerKey?: number;
    /**
     * Top edge animation duration in ms. Applied to both `"fill"`
     * (top is FIRST in the sequence) and `"empty"` (top is LAST).
     * Default 200ms.
     */
    topSpeed?: number;
    /**
     * Side edges (left + right) animation duration in ms. Both sides
     * always animate together. Default 200ms.
     */
    sideSpeed?: number;
    /**
     * Bottom edge animation duration in ms. Applied to both `"fill"`
     * (bottom is LAST) and `"empty"` (bottom is FIRST). Default
     * 200ms.
     */
    bottomSpeed?: number;
}

function resolveColor(color: BorderAnimationColor): string {
    switch (color) {
        case "accent":
            return "rgb(var(--color-accent-base))";
        case "line-base":
            return "rgb(var(--color-line-base))";
        case "foreground-base":
            return "rgb(var(--color-foreground-base))";
        case "ember":
            return "rgb(var(--color-ember-500))";
        default:
            return color;
    }
}

const KEYFRAMES_CSS = `
@keyframes border-anim-top-in {
    0%   { clip-path: inset(0 50% 0 50%); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes border-anim-top-out-left {
    0%   { clip-path: inset(0 50% 0 0); }
    100% { clip-path: inset(0 50% 0 50%); }
}
@keyframes border-anim-top-out-right {
    0%   { clip-path: inset(0 0 0 50%); }
    100% { clip-path: inset(0 50% 0 50%); }
}
@keyframes border-anim-sides-in {
    0%   { clip-path: inset(0 0 100% 0); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes border-anim-sides-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(0 0 100% 0); }
}
@keyframes border-anim-bottom-in-left {
    0%   { clip-path: inset(0 100% 0 0); }
    100% { clip-path: inset(0 50% 0 0); }
}
@keyframes border-anim-bottom-in-right {
    0%   { clip-path: inset(0 0 0 100%); }
    100% { clip-path: inset(0 0 0 50%); }
}
@keyframes border-anim-bottom-out-left {
    0%   { clip-path: inset(0 50% 0 0); }
    100% { clip-path: inset(0 100% 0 0); }
}
@keyframes border-anim-bottom-out-right {
    0%   { clip-path: inset(0 0 0 50%); }
    100% { clip-path: inset(0 0 0 100%); }
}
`;

// Both inset classes are statically present so Tailwind's JIT keeps
// them — we pick at runtime via borderWidth.
const EDGE_BASE_CLASS_1PX = "absolute -inset-px pointer-events-none z-10";
const EDGE_BASE_CLASS_2PX = "absolute -inset-[2px] pointer-events-none z-10";

/**
 * Generic border trace primitive — animates a 1px or 2px border in
 * or out around its nearest positioned ancestor (typically a card
 * with `position: relative`).
 *
 * Composition primitive: caller controls when to mount / what color
 * (with optional top override) / which direction / how fast each
 * edge runs / what thickness. Combine with mount-driven setTimeout
 * chains for "trace in, then bump, then trace out" entrance
 * choreography.
 *
 * Sizing — the component is `position: absolute` with the inset
 * scaled by `borderWidth` (`-inset-px` for 1px, `-inset-[2px]` for
 * 2px) so it overlays the parent's outermost border pixels exactly.
 * The parent must be `position: relative` (or otherwise positioned)
 * for the absolute children to anchor correctly.
 *
 * Renders a fragment, not a div, so it can be dropped into any
 * positioned container without introducing an extra layout node.
 */
export function BorderAnimation({
    direction,
    color = "accent",
    topColor,
    borderWidth = 1,
    triggerKey = 0,
    topSpeed = DEFAULT_TOP_SPEED,
    sideSpeed = DEFAULT_SIDE_SPEED,
    bottomSpeed = DEFAULT_BOTTOM_SPEED
}: BorderAnimationProps): React.ReactElement {
    const edgeColor = resolveColor(color);
    const topEdgeColor = resolveColor(topColor ?? color);
    const edgeBaseClass =
        borderWidth === 2 ? EDGE_BASE_CLASS_2PX : EDGE_BASE_CLASS_1PX;
    const isEmpty = direction === "empty";

    // Per-direction delay schedule. Each step starts after the
    // previous step's duration so the sequence is contiguous (no
    // gaps, no overlaps).
    const fillSidesDelay = topSpeed;
    const fillBottomDelay = topSpeed + sideSpeed;
    const emptySidesDelay = bottomSpeed;
    const emptyTopDelay = bottomSpeed + sideSpeed;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }} />

            {/* Top edge — single element on `fill` (center→out,
                ease-in); two halves on `empty` (each retracting toward
                the center, ease-out). Color follows topColor → color
                fallthrough so a card with an accent top + line-base
                sides can be expressed with one component. */}
            {isEmpty ? (
                <>
                    <div
                        key={`top-l-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderTop: `${borderWidth}px solid ${topEdgeColor}`,
                            clipPath: "inset(0 50% 0 0)",
                            animation: `border-anim-top-out-left ${topSpeed}ms ease-out ${emptyTopDelay}ms both`
                        }}
                    />
                    <div
                        key={`top-r-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderTop: `${borderWidth}px solid ${topEdgeColor}`,
                            clipPath: "inset(0 0 0 50%)",
                            animation: `border-anim-top-out-right ${topSpeed}ms ease-out ${emptyTopDelay}ms both`
                        }}
                    />
                </>
            ) : (
                <div
                    key={`top-${triggerKey}`}
                    className={edgeBaseClass}
                    style={{
                        borderTop: `${borderWidth}px solid ${topEdgeColor}`,
                        animation: `border-anim-top-in ${topSpeed}ms ease-in 0ms both`
                    }}
                />
            )}

            {/* Left edge — single element. `fill` grows top→bottom
                (linear); `empty` retracts bottom→top (linear). */}
            <div
                key={`left-${triggerKey}-${isEmpty}`}
                className={edgeBaseClass}
                style={{
                    borderLeft: `${borderWidth}px solid ${edgeColor}`,
                    animation: isEmpty
                        ? `border-anim-sides-out ${sideSpeed}ms linear ${emptySidesDelay}ms both`
                        : `border-anim-sides-in ${sideSpeed}ms linear ${fillSidesDelay}ms both`
                }}
            />

            {/* Right edge — mirror of left. */}
            <div
                key={`right-${triggerKey}-${isEmpty}`}
                className={edgeBaseClass}
                style={{
                    borderRight: `${borderWidth}px solid ${edgeColor}`,
                    animation: isEmpty
                        ? `border-anim-sides-out ${sideSpeed}ms linear ${emptySidesDelay}ms both`
                        : `border-anim-sides-in ${sideSpeed}ms linear ${fillSidesDelay}ms both`
                }}
            />

            {/* Bottom edge — two halves in both directions. `fill`
                grows from outer corners toward center (ease-out);
                `empty` splits from center back to outer corners
                (ease-in). */}
            {isEmpty ? (
                <>
                    <div
                        key={`bot-l-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderBottom: `${borderWidth}px solid ${edgeColor}`,
                            clipPath: "inset(0 50% 0 0)",
                            animation: `border-anim-bottom-out-left ${bottomSpeed}ms ease-in 0ms both`
                        }}
                    />
                    <div
                        key={`bot-r-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderBottom: `${borderWidth}px solid ${edgeColor}`,
                            clipPath: "inset(0 0 0 50%)",
                            animation: `border-anim-bottom-out-right ${bottomSpeed}ms ease-in 0ms both`
                        }}
                    />
                </>
            ) : (
                <>
                    <div
                        key={`bot-l-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderBottom: `${borderWidth}px solid ${edgeColor}`,
                            clipPath: "inset(0 100% 0 0)",
                            animation: `border-anim-bottom-in-left ${bottomSpeed}ms ease-out ${fillBottomDelay}ms both`
                        }}
                    />
                    <div
                        key={`bot-r-${triggerKey}`}
                        className={edgeBaseClass}
                        style={{
                            borderBottom: `${borderWidth}px solid ${edgeColor}`,
                            clipPath: "inset(0 0 0 100%)",
                            animation: `border-anim-bottom-in-right ${bottomSpeed}ms ease-out ${fillBottomDelay}ms both`
                        }}
                    />
                </>
            )}
        </>
    );
}
